import React, { type ComponentType, useState } from "react";
import { Checkbox, Paper } from "@mui/material";

import EmailLocation from "./EmailLocation";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import useSearchFilter from "@/helpers/hooks/useSearchFilter";
import JoinEmailCampaignBulkSelection from "../bulk-selection/JoinEmailCampaignBulkSelection";

type LocationId = string | number;

type EmailTemplate = {
  id: LocationId;
  [key: string]: unknown;
};

type EmailLocationData = {
  id: LocationId;
  name: string;
  [key: string]: unknown;
};

type EmailCampaign = {
  templates: EmailTemplate[];
  [key: string]: unknown;
};

type EmailLocationFormValue = Record<string, unknown>;

type EmailLocationsProps = {
  values: Record<LocationId, EmailLocationFormValue>;
  setFieldValue: (_field: LocationId, _value: unknown) => void;
  campaign: EmailCampaign;
  expanded: LocationId[];
  setExpanded: (_expanded: LocationId[]) => void;
  locations: EmailLocationData[];
};

type BulkSelectionProps = {
  templates?: EmailTemplate[];
  values?: Record<LocationId, EmailLocationFormValue>;
  setFieldValue: (_field: LocationId, _value: unknown) => void;
  checkedLocations?: LocationId[];
  setCheckedLocations?: React.Dispatch<React.SetStateAction<LocationId[]>>;
  locations?: EmailLocationData[];
  setLocationExpanded: (_expanded: LocationId[]) => void;
  isSelectAll?: boolean;
  onSelectAll?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  setShowCheckbox: (_show: boolean) => void;
  campaign?: EmailCampaign;
};

type EmailLocationProps = {
  setExpanded: (_expanded: LocationId[]) => void;
  loc: EmailLocationData;
  locFormData?: EmailLocationFormValue;
  setFieldValue: (_field: LocationId, _value: unknown) => void;
  templates: EmailTemplate[];
  shouldExpandIt: boolean;
};

const BulkSelection =
  JoinEmailCampaignBulkSelection as ComponentType<BulkSelectionProps>;
const EmailLocationRow = EmailLocation as ComponentType<EmailLocationProps>;

function EmailLocations({
  values,
  setFieldValue,
  campaign,
  expanded,
  setExpanded,
  locations,
}: EmailLocationsProps) {
  const [checkedLocations, setCheckedLocations] = useState<LocationId[]>([]);
  const [showCheckbox, setShowCheckbox] = useState(false);

  const [searchKey, handleSearchChange, filteredSubscribedLocations] =
    useSearchFilter(locations, ["name"]) as [
      string,
      (_value: string) => void,
      EmailLocationData[],
    ];

  return (
    <div className="mt-3">
      <BulkSelection
        templates={campaign.templates}
        values={values}
        setFieldValue={setFieldValue}
        checkedLocations={checkedLocations}
        setCheckedLocations={setCheckedLocations}
        locations={locations}
        setLocationExpanded={setExpanded}
        isSelectAll={checkedLocations.length === locations.length}
        onSelectAll={(e) => {
          if (e.target.checked) {
            setCheckedLocations((_prev) => locations.map((loc) => loc.id));
          } else {
            setCheckedLocations([]);
          }
        }}
        setShowCheckbox={setShowCheckbox}
        campaign={campaign}
      />

      <div className="row mb-2 align-items-center">
        <div className="col-md-6 d-flex align-items-center p-xs-0">
          {showCheckbox && (
            <Paper
              className="p-0 mr-1 d-inline-flex align-items-center"
              elevation={2}
            >
              <Checkbox
                color="primary"
                checked={checkedLocations.length === locations.length}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    setCheckedLocations((_prev) =>
                      locations.map((loc) => loc.id)
                    );
                  } else {
                    setCheckedLocations([]);
                  }
                }}
                className="m-0 p-1"
              />
            </Paper>
          )}
          <MuiSearchInput value={searchKey} onChange={handleSearchChange} />
        </div>
      </div>

      {filteredSubscribedLocations.map((loc) => (
        <div
          className="d-flex align-items-start justify-content-center"
          key={loc.id}
        >
          {showCheckbox && (
            <Paper className="mr-1 mt-0 p-0" elevation={2}>
              <Checkbox
                color="primary"
                checked={checkedLocations.includes(loc.id)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    setCheckedLocations((prev) => [...prev, loc.id]);
                  } else {
                    setCheckedLocations((prev) =>
                      prev.filter((id) => id !== loc.id)
                    );
                  }
                }}
                className="m-0 p-1"
              />
            </Paper>
          )}
          <div className="w-100">
            <EmailLocationRow
              setExpanded={setExpanded}
              loc={loc}
              locFormData={values[loc.id]}
              setFieldValue={setFieldValue}
              templates={campaign.templates}
              shouldExpandIt={expanded.includes(loc.id)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default EmailLocations;
